import { type RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { joinValuesText } from './joinValues';
import { shortenValue } from './shortenValue';
import type { DescriptionPart } from './types';

/** Кусок описания, ширину которого снимает слой замера */
export type DescriptionMeasureItem = {
  /** Ключ куска: одинаковые по разметке куски меряются один раз */
  key: string;
} & (
  | { kind: 'lead' | 'separator' | 'clipped' }
  | {
      kind: 'part';
      part: DescriptionPart;
      /** Сколько значений части свёрнуто в счётчик */
      hiddenCount: number;
      /** Предел длины значений части в знаках; `null` — предела нет */
      valueLimit: number | null;
    }
);

/** Умещение описания отбора в одну строку */
type Result = {
  /** Строка описания целиком — её ширина и задаёт, что помещается */
  containerRef: (element: HTMLDivElement | null) => void;
  /** Слой замера: в нём стоят те куски описания, ширины которых нужны расчёту */
  measureRef: RefObject<HTMLDivElement | null>;
  /** Что отрисовать в слое замера; `null` — ширины сняты, слой больше не нужен */
  measureItems: DescriptionMeasureItem[] | null;
  /** Сколько частей описания показано; остальные ушли из строки целиком, с конца */
  visibleCount: number;
  /** Идентификатор части → сколько её значений свёрнуто в счётчик */
  hiddenCounts: Record<string, number>;
  /** Идентификатор части → предел длины её значений в знаках; `null` — предела нет */
  valueLimits: Record<string, number | null>;
};

/** Умещение описания: до каких уступок дошли части, чтобы строка сошлась по ширине */
type FitState = {
  /** Сколько частей ушло из строки целиком — они уступают последними, с правого края */
  droppedCount: number;
  hiddenCounts: Record<string, number>;
  /** Идентификатор части → индекс её ступени в `TEXT_LIMITS` */
  limitSteps: Record<string, number>;
};

/** Снятые ширины кусков описания того состава, для которого они сняты */
type Metrics = {
  /** Состав описания, к которому относятся ширины */
  signature: string;
  /** Ключ куска → его ширина в пикселях */
  widths: Record<string, number>;
};

/**
 * Ступени предела длины значения в знаках — от свободной к тесной. Значение уступает ступенями,
 * пока не поместится; первая ступень — предела нет, значение показано целиком.
 */
const TEXT_LIMITS: readonly (number | null)[] = [null, 18, 13, 9, 6, 4, 2, 1];

const LAST_LIMIT_STEP = TEXT_LIMITS.length - 1;

/** Умещение свободнее некуда: части все в строке, значения на виду и показаны целиком */
const RELAXED: FitState = { droppedCount: 0, hiddenCounts: {}, limitSteps: {} };

/**
 * Запас ширины в пикселях: сумма замеренных кусков и фактическая ширина строки расходятся на доли
 * пикселя округлением, и без запаса описание встало бы впритык — с риском срезать край без
 * многоточия.
 */
const MEASURE_TOLERANCE = 1;

/** Ключи кусков описания, общих для всех его частей */
const LEAD_KEY = 'lead';
const SEPARATOR_KEY = 'separator';
const CLIPPED_KEY = 'clipped';

/** Состав описания: тот же состав — то же умещение, даже если описание пересобрано заново */
const getSignature = (parts: DescriptionPart[]): string =>
  parts.map((part) => `${part.id}=${part.label}:${part.values.map((value) => value.text).join(',')}`).join('|');

/**
 * Ключ части в её состоянии: части, отрисованные одинаково, занимают одинаковую ширину — их
 * достаточно замерить один раз, сколько бы уступок к одному и тому же виду ни приводило.
 */
const getPartKey = (part: DescriptionPart, hiddenCount: number, valueLimit: number | null): string => {
  const visibleValues = part.values.slice(0, part.values.length - hiddenCount);
  return `${part.id}|${hiddenCount}|${shortenValue(joinValuesText(visibleValues), valueLimit)}`;
};

/** Предел длины значений части на её текущей ступени */
const getValueLimit = (state: FitState, part: DescriptionPart): number | null =>
  TEXT_LIMITS[state.limitSteps[part.id] ?? 0] ?? null;

/**
 * Следующая уступка: её делает самая правая часть, которой ещё есть чем уступить. Перечисление
 * нескольких значений вместо усечения сворачивается — остаётся первое значение и счётчик
 * оставшихся; остальные значения усекаются многоточием ступенями. Часть сортировки текстом не
 * уступает вовсе: она названа колонкой, а названия колонок целы на любой ширине.
 *
 * Когда усекать в показанных частях уже нечего, последняя часть уходит из строки целиком — со
 * своим названием, значениями и управлением: срезанное краем название читается как другое слово,
 * а срезанная кнопка не нажимается. `null` — уступать больше нечем.
 */
const concede = (parts: DescriptionPart[], state: FitState): FitState | null => {
  const visibleParts = parts.slice(0, parts.length - state.droppedCount);

  for (let index = visibleParts.length - 1; index >= 0; index--) {
    const part = visibleParts[index];
    if (part.kind === 'sorting') continue;

    const visibleValues = part.values.length - (state.hiddenCounts[part.id] ?? 0);
    if (part.isFoldable && visibleValues > 1) {
      return { ...state, hiddenCounts: { ...state.hiddenCounts, [part.id]: part.values.length - 1 } };
    }

    const step = state.limitSteps[part.id] ?? 0;
    if (step < LAST_LIMIT_STEP) {
      return { ...state, limitSteps: { ...state.limitSteps, [part.id]: step + 1 } };
    }
  }

  // С уходом части место освобождается, поэтому оставшиеся начинают уступать заново — иначе они
  // так и остались бы сжатыми до предела на ширине, где помещаются целиком.
  // Последняя часть в строке остаётся: описание без единой названной части читать нечем
  if (visibleParts.length > 1) {
    return { droppedCount: state.droppedCount + 1, hiddenCounts: {}, limitSteps: {} };
  }

  return null;
};

/**
 * Состояния части, до которых её доводят уступки: свёртывание перечисления идёт раньше усечения,
 * а дальше значения усекаются ступенями. Часть сортировки не уступает ничем — вид у неё один.
 */
const getPartStates = (part: DescriptionPart): { hiddenCount: number; valueLimit: number | null }[] => {
  if (part.kind === 'sorting') return [{ hiddenCount: 0, valueLimit: null }];

  const isFolded = part.isFoldable && part.values.length > 1;
  const states = isFolded ? [{ hiddenCount: 0, valueLimit: null }] : [];
  const hiddenCount = isFolded ? part.values.length - 1 : 0;

  return [...states, ...TEXT_LIMITS.map((valueLimit) => ({ hiddenCount, valueLimit }))];
};

/** Все куски, из ширин которых складывается любой вид описания: значок, части, разделители, обрыв */
const buildMeasureItems = (parts: DescriptionPart[]): DescriptionMeasureItem[] => {
  const items: DescriptionMeasureItem[] = [
    { key: LEAD_KEY, kind: 'lead' },
    { key: SEPARATOR_KEY, kind: 'separator' },
    { key: CLIPPED_KEY, kind: 'clipped' },
  ];
  const keys = new Set(items.map((item) => item.key));

  parts.forEach((part) => {
    getPartStates(part).forEach(({ hiddenCount, valueLimit }) => {
      const key = getPartKey(part, hiddenCount, valueLimit);
      if (keys.has(key)) return;
      keys.add(key);
      items.push({ key, kind: 'part', part, hiddenCount, valueLimit });
    });
  });

  return items;
};

/**
 * Умещение под заданную ширину: уступки идут по тем же правилам, но ширина каждого их шага
 * известна заранее из замера, поэтому весь ряд уступок проходится счётом — сколько бы их ни
 * потребовалось, описание отрисовывается сразу в окончательном виде.
 */
const computeFit = (parts: DescriptionPart[], available: number, widths: Record<string, number>): FitState => {
  const getWidth = (key: string): number => widths[key] ?? 0;

  const getTotalWidth = (state: FitState): number => {
    const visibleCount = parts.length - state.droppedCount;
    const partsWidth = parts
      .slice(0, visibleCount)
      .reduce(
        (total, part) =>
          total + getWidth(getPartKey(part, state.hiddenCounts[part.id] ?? 0, getValueLimit(state, part))),
        0,
      );
    const separatorsWidth = getWidth(SEPARATOR_KEY) * Math.max(visibleCount - 1, 0);
    const clippedWidth = state.droppedCount > 0 ? getWidth(CLIPPED_KEY) : 0;

    return getWidth(LEAD_KEY) + partsWidth + separatorsWidth + clippedWidth;
  };

  let state = RELAXED;
  while (getTotalWidth(state) > available - MEASURE_TOLERANCE) {
    const next = concede(parts, state);
    if (!next) break;
    state = next;
  }

  return state;
};

/**
 * Держит описание отбора в одной строке уступками правого края: не поместившееся отдаёт последняя
 * часть описания, а когда ей уступать нечем — предыдущая, и так справа налево. Уступает только
 * текст значений: подписи колонок, связки и элементы управления не сокращаются и остаются
 * видимыми и нажимаемыми — не поместившаяся часть вместо этого уходит из строки целиком.
 *
 * Замер идёт по разметке — сколько значений и знаков поместится в строку, из состояния таблицы не
 * вывести. Меряются не сами уступки одна за другой, а все виды, которые описание вообще может
 * принять: ширины снимаются разом со скрытого слоя, и дальше нужный вид считается по ним, без
 * повторной отрисовки. Умещение привязано к ширине строки, поэтому на каждое её изменение оно
 * считается заново: на возвращённой ширине описание снова показано целиком.
 */
export const useDescriptionFit = (parts: DescriptionPart[]): Result => {
  // Строка описания показывается не всегда, поэтому она держится состоянием, а не ссылкой:
  // так замер и наблюдение за шириной начинаются ровно тогда, когда строка появилась
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);

  const signature = useMemo(() => getSignature(parts), [parts]);
  const measureItems = useMemo(() => buildMeasureItems(parts), [parts]);
  // Снятые ширины относятся к своему составу описания: с другим составом их снимают заново
  const isMeasured = metrics?.signature === signature;

  // Ширина строки меняется вместе с областью списка, а описание об этом иначе не узнаёт
  useLayoutEffect(() => {
    if (!container) return;

    const readWidth = () => setWidth(container.clientWidth);
    readWidth();

    const observer = new ResizeObserver(readWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, [container]);

  useLayoutEffect(() => {
    const layer = measureRef.current;
    if (isMeasured || !layer) return;

    // Куски стоят в слое в том же порядке, в каком заказаны, — каждый в своей обёртке
    const widths: Record<string, number> = {};
    measureItems.forEach((item, index) => {
      const element = layer.children[index];
      if (element) widths[item.key] = element.getBoundingClientRect().width;
    });

    setMetrics({ signature, widths });
  }, [isMeasured, measureItems, signature]);

  // Ширины сняты в том шрифте, каким описание нарисовано в момент замера: пока свой шрифт ещё
  // грузится, буквы шире или уже, и снятое по подменному шрифту устаревает — замер повторяется
  useEffect(() => {
    let isMounted = true;
    document.fonts?.ready.then(() => {
      if (isMounted) setMetrics(null);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const fit = useMemo(
    () => (width === null || !isMeasured || !metrics ? RELAXED : computeFit(parts, width, metrics.widths)),
    [parts, width, isMeasured, metrics],
  );

  const valueLimits = useMemo(
    () => Object.fromEntries(parts.map((part) => [part.id, getValueLimit(fit, part)])) as Record<string, number | null>,
    [parts, fit],
  );

  return {
    containerRef: setContainer,
    measureRef,
    measureItems: isMeasured ? null : measureItems,
    visibleCount: parts.length - fit.droppedCount,
    hiddenCounts: fit.hiddenCounts,
    valueLimits,
  };
};
